import { Button } from '../ui/button';
import { ArrowRight, BarChart3, Package, Database, TrendingUp, Target, Users, MapPin, CheckCircle2 } from 'lucide-react';
import { WA_URL } from '../../lib/wa';

const PROBLEMS = [
  'No visibility into actual consumer demand',
  'Months of waiting to know if products sell',
  'No feedback on pricing or packaging decisions',
  'Regional performance remains a mystery',
  'Costly mistakes discovered too late',
];

const SOLUTIONS = [
  'Real-time sales data from actual consumers',
  'Weekly performance reports and insights',
  'Pricing sensitivity analysis by region',
  'Pack size and SKU performance metrics',
  'Territory-wise demand patterns',
];

const FEATURES = [
  { Icon: TrendingUp, title: 'Sales Velocity', body: 'Track which products move fast and which need adjustment' },
  { Icon: Target, title: 'Price Optimization', body: 'Understand price sensitivity and maximize margins' },
  { Icon: Users, title: 'Consumer Insights', body: 'Demographics, preferences, and buying patterns' },
  { Icon: MapPin, title: 'Regional Heat Maps', body: 'See where your products perform best geographically' },
];

export const MarketIntelligence = () => (
  <section className="py-20 md:py-32 bg-gradient-to-br from-blue-50 to-white relative overflow-hidden">
    <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
    <div className="container mx-auto px-6 relative z-10">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-[#004aad] text-white px-5 py-2 rounded-full text-sm font-semibold mb-6">
            <BarChart3 className="h-4 w-4" />
            <span>CORE COMPETITIVE ADVANTAGE</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            Stop Guessing.
            <span className="block text-[#004aad] mt-2">Start Knowing.</span>
          </h2>
          <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Unlike traditional exporters who leave you in the dark, Atlas provides real-time sales intelligence that transforms how you approach the ANZ market.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-gray-100">
            <div className="flex items-start space-x-4 mb-6">
              <div className="h-12 w-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Package className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Traditional Exporters</h3>
                <p className="text-gray-600 text-lg">The "Ship and Hope" Model</p>
              </div>
            </div>
            <ul className="space-y-4">
              {PROBLEMS.map((p) => (
                <li key={p} className="flex items-start space-x-3">
                  <div className="h-6 w-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-red-600 text-sm font-bold">✕</span>
                  </div>
                  <span className="text-gray-700">{p}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-gradient-to-br from-[#004aad] to-[#0066cc] rounded-2xl p-8 shadow-2xl border-2 border-[#004aad] transform lg:scale-105">
            <div className="flex items-start space-x-4 mb-6">
              <div className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center flex-shrink-0">
                <Database className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-2">Atlas Intelligence</h3>
                <p className="text-blue-100 text-lg">Data-Driven Market Entry</p>
              </div>
            </div>
            <ul className="space-y-4">
              {SOLUTIONS.map((s) => (
                <li key={s} className="flex items-start space-x-3">
                  <div className="h-6 w-6 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle2 className="h-4 w-4 text-white" />
                  </div>
                  <span className="text-white font-medium">{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {FEATURES.map(({ Icon, title, body }) => (
            <div key={title} className="bg-white rounded-xl p-6 shadow-md border border-gray-100 hover:shadow-lg transition-shadow">
              <div className="h-10 w-10 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                <Icon className="h-5 w-5 text-[#004aad]" />
              </div>
              <h4 className="font-bold text-gray-900 mb-2">{title}</h4>
              <p className="text-gray-600 text-sm">{body}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-lg text-gray-700 mb-6 max-w-2xl mx-auto">
            <strong className="text-[#004aad]">This intelligence is your competitive edge.</strong> Make informed decisions on inventory, pricing, packaging, and expansion strategy—backed by real market data, not guesswork.
          </p>
          <Button asChild size="lg" className="bg-[#004aad] hover:bg-[#003a8c] text-white text-lg px-10 py-6 transition-all duration-300 hover:shadow-xl" data-testid="intelligence-cta-btn">
            <a href={WA_URL} target="_blank" rel="noopener noreferrer">
              See How Our Intelligence Works
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
          </Button>
        </div>
      </div>
    </div>
  </section>
);
